import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import StarRating from "./StarRating";
import { toast } from "@/hooks/use-toast";
import { MAJORS, getReviewTypeConfig, REVIEW_TYPES, type ReviewType } from "@/lib/mockData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { findProfaneWords } from "@/lib/profanityFilter";
import { checkReviewQuality, checkFieldQuality } from "@/lib/gibberishFilter";
import { logRejection } from "@/lib/logRejection";
import Turnstile from "@/components/Turnstile";
import { verifyTurnstile } from "@/lib/verifyTurnstile";

interface RatingFormProps {
  type: ReviewType;
  open: boolean;
  onClose: () => void;
  onSubmitted?: (submittedType: ReviewType) => void;
  initialOrganization?: string;
  initialCategory?: string | null;
}

const RatingForm = ({ type, open, onClose, onSubmitted, initialOrganization, initialCategory }: RatingFormProps) => {
  const [selectedType, setSelectedType] = useState<ReviewType>(type);
  const [rating, setRating] = useState(0);
  const [difficulty, setDifficulty] = useState(0);
  const [wouldDoAgain, setWouldDoAgain] = useState<boolean | null>(null);
  const [title, setTitle] = useState("");
  const [organization, setOrganization] = useState(initialOrganization ?? "");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [isRemote, setIsRemote] = useState(false);
  const [major, setMajor] = useState("");
  const [majorFocused, setMajorFocused] = useState(false);
  const [category, setCategory] = useState(initialCategory ?? "");
  const [categoryOther, setCategoryOther] = useState("");
  const [categoryFocused, setCategoryFocused] = useState(false);
  const [compensation, setCompensation] = useState("");
  const [interviewInfo, setInterviewInfo] = useState("");
  const [responsibilities, setResponsibilities] = useState("");


  const majorQuery = major.trim().toLowerCase();
  const majorSuggestions = majorQuery
    ? MAJORS.filter((m) => m.toLowerCase().includes(majorQuery)).slice(0, 8)
    : MAJORS.slice(0, 8);

  const cfg = getReviewTypeConfig(selectedType);
  const categorySource = cfg.categories;
  const categoryQuery = category.trim().toLowerCase();
  const categorySuggestions = categoryQuery
    ? categorySource.filter((c) => c.toLowerCase().includes(categoryQuery)).slice(0, 8)
    : categorySource.slice(0, 8);
  const [review, setReview] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [supervisorFirstName, setSupervisorFirstName] = useState("");
  const [supervisorLastName, setSupervisorLastName] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  useEffect(() => {
    if (open) setSelectedType(type);
  }, [open, type]);

  const handleTypeChange = (value: string) => {
    const next = value as ReviewType;
    setSelectedType(next);
    setCategory("");
    setSelectedTags([]);
  };

  const tags = cfg.tags;


  const showCompensation = cfg.showCompensation;
  const detailFields = [review, ...(showCompensation ? [compensation] : []), interviewInfo, responsibilities];
  const detailCompletion = Math.round(
    (detailFields.filter((f) => f.trim().length > 0).length / detailFields.length) * 100
  );


  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : prev.length < 5 ? [...prev, tag] : prev
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) {
      toast({ title: "Please select a rating", variant: "destructive" });
      return;
    }
    if (difficulty === 0) {
      toast({ title: "Please select a difficulty", variant: "destructive" });
      return;
    }
    const finalCategory = category.trim();
    if (!finalCategory) {
      toast({ title: `Please enter a ${cfg.label.toLowerCase()} type`, variant: "destructive" });
      return;
    }
    if (!organization.trim()) {
      toast({
        title: `Please enter the ${cfg.orgLabel.toLowerCase()} name`,
        variant: "destructive",
      });
      return;
    }
    if (cfg.requiresTitle && !title.trim()) {
      toast({ title: "Please enter the position title", variant: "destructive" });
      return;
    }
    const fieldChecks: Array<[string, string]> = [
      [finalCategory, cfg.categoryLabel],
      [organization, cfg.orgLabel],
      [title, "Position title"],
      [major, "Major"],
      [supervisorFirstName, "Supervisor first name"],
      [supervisorLastName, "Supervisor last name"],
      [city, "City"],
      [state, "State"],
    ];
    for (const [val, label] of fieldChecks) {
      const fq = checkFieldQuality(val, label);
      if (!fq.ok) {
        toast({ title: "Please check your entry", description: fq.reason, variant: "destructive" });
        return;
      }
    }
    // Reviews now require substantive content (75+ chars, 15+ words)
    const quality = checkReviewQuality(review);
    if (!quality.ok) {
      toast({
        title: "Please improve your review",
        description: quality.reason,
        variant: "destructive",
      });
      logRejection({ kind: "review", content: review, title, reason: quality.reason || "quality" });
      return;
    }
    const offending = Array.from(new Set([
      ...findProfaneWords(review),
      ...findProfaneWords(organization),
    ]));
    if (offending.length > 0) {
      toast({
        title: "Please adjust your language",
        description: `Your submission contains vulgar or offensive language (${offending.slice(0, 3).join(", ")}${offending.length > 3 ? "…" : ""}). Please rephrase before posting.`,
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    const ok = await verifyTurnstile(captchaToken, "review_submit");
    if (!ok) {
      setSubmitting(false);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      toast({
        title: "Verification failed",
        description: "Please complete the security check and try again.",
        variant: "destructive",
      });
      return;
    }

    const location = isRemote ? "Remote" : `${city}, ${state}`;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setSubmitting(false);
      toast({ title: "Please sign in to post a review", variant: "destructive" });
      return;
    }

    const toDate = (m: string) => (m && /^\d{4}-\d{2}$/.test(m) ? `${m}-01` : null);
    const startDate = toDate(startMonth);
    const endDate = toDate(endMonth);
    if (startDate && endDate && endDate < startDate) {
      setSubmitting(false);
      toast({ title: "Invalid dates", description: "End month must be after start month.", variant: "destructive" });
      return;
    }

    const { error } = await supabase.from("reviews").insert({
      type: selectedType,
      title: cfg.requiresTitle ? title : organization,
      organization,
      location,
      rating,
      difficulty,
      would_do_again: wouldDoAgain,
      review,
      major: major ? `${finalCategory} • ${major}` : finalCategory,
      author: "Anonymous Student",
      tags: selectedTags,
      user_id: user.id,
      start_date: startDate,
      end_date: endDate,
      compensation: showCompensation ? compensation.trim() || null : null,
      interview_info: interviewInfo.trim() || null,
      responsibilities: responsibilities.trim() || null,
    } as any);


    setSubmitting(false);

    if (error) {
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      const msg = error.message || "";
      const isRate = /wait at least/i.test(msg);
      const isYearly = /30 reviews per year/i.test(msg);
      const isDup = /Duplicate review/i.test(msg);
      let title = "Failed to submit review";
      let description: string = msg;
      if (isYearly) { title = "Yearly limit reached"; description = "You can post up to 30 reviews per year."; }
      else if (isRate) { title = "Please slow down"; description = "Please wait a few minutes before posting another review."; }
      else if (isDup) { title = "Already submitted"; description = "You've already posted a very similar review. Please write about a different experience."; }
      toast({ title, description, variant: "destructive" });
      logRejection({ kind: "review", content: review, title: cfg.requiresTitle ? title : organization, reason: msg });
      return;
    }

    toast({
      title: "Review submitted!",
      description: `Your ${selectedType} review has been submitted successfully.`,
    });
    setRating(0);
    setDifficulty(0);
    setWouldDoAgain(null);
    setTitle("");
    setOrganization("");
    setCity("");
    setState("");
    setIsRemote(false);
    setMajor("");
    setCategory("");
    setCategoryOther("");
    setReview("");
    setSelectedTags([]);
    setSupervisorFirstName("");
    setSupervisorLastName("");
    setStartMonth("");
    setEndMonth("");
    setCompensation("");
    setInterviewInfo("");
    setResponsibilities("");
    onSubmitted?.(selectedType);
    onClose();
  };


  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-card-foreground">
                Rate a {cfg.label}
              </h2>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="review-type">Experience Type</Label>
                <Select value={selectedType} onValueChange={handleTypeChange}>
                  <SelectTrigger id="review-type">
                    <SelectValue placeholder="Select experience type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {getReviewTypeConfig(t).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">
                  {cfg.categoryLabel}
                </Label>
                <div className="relative">
                  <Input
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    onFocus={() => setCategoryFocused(true)}
                    onBlur={() => setTimeout(() => setCategoryFocused(false), 150)}
                    placeholder={cfg.categoryPlaceholder}
                    autoComplete="off"
                    required
                  />
                  {categoryFocused && categorySuggestions.length > 0 && (
                    <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                      {categorySuggestions.map((c) => (
                        <li key={c}>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setCategory(c);
                              setCategoryFocused(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                          >
                            {c}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="major-top">Your Major (optional)</Label>
                <div className="relative">
                  <Input
                    id="major-top"
                    value={major}
                    onChange={(e) => setMajor(e.target.value)}
                    onFocus={() => setMajorFocused(true)}
                    onBlur={() => setTimeout(() => setMajorFocused(false), 150)}
                    placeholder="Start typing… e.g. Nursing, Biology, Public Health"
                    autoComplete="off"
                  />
                  {majorFocused && majorSuggestions.length > 0 && (
                    <ul className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                      {majorSuggestions.map((m) => (
                        <li key={m}>
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setMajor(m);
                              setMajorFocused(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                          >
                            {m}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="supervisor-name">Preceptor / Supervisor Name (optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id="supervisor-first-name"
                    value={supervisorFirstName}
                    onChange={(e) => setSupervisorFirstName(e.target.value)}
                    placeholder="First Name"
                  />
                  <Input
                    id="supervisor-last-name"
                    value={supervisorLastName}
                    onChange={(e) => setSupervisorLastName(e.target.value)}
                    placeholder="Last Name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Overall Rating</Label>
                  <StarRating rating={rating} size="lg" interactive onRatingChange={setRating} />
                </div>
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <StarRating rating={difficulty} size="lg" interactive onRatingChange={setDifficulty} />
                </div>
              </div>

              {/* Would do again */}
              <div className="space-y-2">
                <Label>Would you do this again?</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={wouldDoAgain === true ? "default" : "outline"}
                    onClick={() => setWouldDoAgain(true)}
                  >
                    👍 Yes
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={wouldDoAgain === false ? "default" : "outline"}
                    onClick={() => setWouldDoAgain(false)}
                  >
                    👎 No
                  </Button>
                </div>
              </div>

              {cfg.requiresTitle && (
                <div className="space-y-2">
                  <Label htmlFor="title">Position Title</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Software Engineering Intern"
                    required
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="organization">
                  {cfg.orgLabel}
                </Label>
                <Input
                  id="organization"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder={cfg.orgPlaceholder}
                  required
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label>Location</Label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="remote-toggle"
                    checked={isRemote}
                    onChange={(e) => setIsRemote(e.target.checked)}
                    className="rounded border-input"
                  />
                  <Label htmlFor="remote-toggle" className="text-sm font-normal cursor-pointer">Remote</Label>
                </div>
                {!isRemote && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                      required={!isRemote}
                    />
                    <Input
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="State"
                      required={!isRemote}
                    />
                  </div>
                )}
              </div>

              {/* Time frame (optional) */}
              <div className="space-y-2">
                <Label>Time Frame (optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="start-month" className="text-xs font-normal text-muted-foreground">Start</Label>
                    <Input
                      id="start-month"
                      type="month"
                      value={startMonth}
                      onChange={(e) => setStartMonth(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-month" className="text-xs font-normal text-muted-foreground">End</Label>
                    <Input
                      id="end-month"
                      type="month"
                      value={endMonth}
                      onChange={(e) => setEndMonth(e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Leave blank if you'd rather not share.</p>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>Tags (select up to 5)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? "default" : "outline"}
                      className="cursor-pointer text-xs transition-colors"
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="review">Your Review (optional)</Label>
                  <span className={`text-xs ${review.trim().split(/\s+/).filter(Boolean).length > 2000 ? "text-destructive" : "text-muted-foreground"}`}>
                    {review.trim() ? review.trim().split(/\s+/).filter(Boolean).length : 0}/2,000 words
                  </span>
                </div>
                <Textarea
                  id="review"
                  value={review}
                  onChange={(e) => {
                    const words = e.target.value.trim().split(/\s+/).filter(Boolean).length;
                    if (words <= 2000 || e.target.value.length < review.length) {
                      setReview(e.target.value);
                    }
                  }}
                  placeholder="Share your experience... (optional)"
                  rows={4}
                />
              </div>

              {/* Optional details that push this experience toward 100% documented */}
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Help complete this experience (optional)</Label>
                  <span className="text-xs font-semibold text-primary">{detailCompletion}% complete</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${detailCompletion}%` }} />
                </div>
                {showCompensation && (
                  <div className="space-y-1.5">
                    <Label htmlFor="compensation" className="text-xs font-normal text-muted-foreground">Compensation / stipend</Label>
                    <Input id="compensation" value={compensation} onChange={(e) => setCompensation(e.target.value)} placeholder="e.g. Unpaid, $22/hr, $5,000 stipend" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="interview-info" className="text-xs font-normal text-muted-foreground">Interview / application process</Label>
                  <Textarea id="interview-info" rows={2} value={interviewInfo} onChange={(e) => setInterviewInfo(e.target.value)} placeholder="How did you get in? Interview format, timeline…" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="responsibilities" className="text-xs font-normal text-muted-foreground">What did you actually do?</Label>
                  <Textarea id="responsibilities" rows={2} value={responsibilities} onChange={(e) => setResponsibilities(e.target.value)} placeholder="Day-to-day responsibilities" />
                </div>
              </div>


              <div className="flex justify-center">
                <Turnstile key={captchaKey} action="review_submit" onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} onError={() => setCaptchaToken(null)} />
              </div>
              <Button type="submit" className="w-full" disabled={submitting || !captchaToken}>
                {submitting ? "Verifying..." : "Submit Review"}
              </Button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RatingForm;
